import React from 'react';
import StandardQuickFilters from '../StandardComponents/StandardQuickFilters/StandardQuickFilters';
import BusinessIcon from '@mui/icons-material/Business';
import VerifiedIcon from '@mui/icons-material/Verified';
import PendingIcon from '@mui/icons-material/Pending';
import UpdateIcon from '@mui/icons-material/Update';

/**
 * OrganizationQuickFilters Component
 * 
 * Organization List page specific implementation of quick filters
 * Uses the generic StandardQuickFilters component with Organization-specific configuration
 */
const OrganizationQuickFilters = ({ rows = [], activeFilters = {}, onApplyFilter }) => {
  // Calculate counts for different filter types
  const approvedCount = rows.filter(row => 
    row.status === 'Approved'
  ).length;
  
  const pendingCount = rows.filter(row => 
    row.status === 'Pending Approval'
  ).length;
  
  const trialCount = rows.filter(row => 
    row.subscription_type === 'Trial'
  ).length;
  
  const recentlyUpdatedCount = rows.filter(row => {
    const updatedAt = new Date(row.updated_at);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return updatedAt > thirtyDaysAgo;
  }).length;

  // Define filter configurations for Organization List page
  const organizationFilters = [
    {
      type: 'status',
      label: 'Approved',
      field: 'status',
      value: 'Approved',
      count: approvedCount,
      tooltip: 'Show approved organizations',
      colors: {
        active: {
          bg: '#E8F5E9',
          text: '#2E7D32',
          border: '#4CAF50'
        },
        default: {
          bg: '#F9FAFB',
          text: '#374151',
          border: '#E5E7EB'
        },
        hover: {
          bg: '#E8F5E9',
          text: '#2E7D32',
          border: '#4CAF50'
        }
      },
      icon: <VerifiedIcon style={{ color: '#4CAF50', fontSize: 20 }} />
    },
    {
      type: 'status',
      label: 'Pending Approval',
      field: 'status',
      value: 'Pending Approval',
      count: pendingCount,
      tooltip: 'Show organizations pending approval',
      colors: {
        active: {
          bg: '#FFF3E0',
          text: '#E65100',
          border: '#FF9800'
        },
        default: {
          bg: '#F9FAFB',
          text: '#374151',
          border: '#E5E7EB'
        },
        hover: {
          bg: '#FFF3E0',
          text: '#E65100',
          border: '#FF9800'
        }
      },
      icon: <PendingIcon style={{ color: '#FF9800', fontSize: 20 }} />
    },
    {
      type: 'subscription',
      label: 'Trial Subscription',
      field: 'subscription_type',
      value: 'Trial',
      count: trialCount,
      tooltip: 'Show organizations with trial subscription',
      colors: {
        active: {
          bg: '#E3F2FD',
          text: '#1565C0',
          border: '#2196F3'
        },
        default: {
          bg: '#F9FAFB',
          text: '#374151',
          border: '#E5E7EB'
        },
        hover: {
          bg: '#E3F2FD',
          text: '#1565C0',
          border: '#2196F3'
        }
      },
      icon: <BusinessIcon style={{ color: '#2196F3', fontSize: 20 }} />
    },
    {
      type: 'date',
      label: 'Recently Updated',
      field: 'recently_updated',
      value: true,
      count: recentlyUpdatedCount,
      tooltip: 'Show organizations updated in the last 30 days',
      colors: {
        active: {
          bg: '#EFF6FF',
          text: '#1E40AF',
          border: '#3B82F6'
        },
        default: {
          bg: '#F9FAFB',
          text: '#374151',
          border: '#E5E7EB'
        },
        hover: {
          bg: '#EFF6FF',
          text: '#1E40AF',
          border: '#3B82F6'
        }
      },
      icon: <UpdateIcon style={{ color: '#3B82F6', fontSize: 20 }} />
    }
  ];

  // Custom styles for matching ActiveFilters appearance
  const customStyles = {
    container: {
      py: 1,
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      mb: 2,
      px: 2
    },
    title: {
      color: '#111827',
      fontSize: '15px'
    },
    filtersContainer: {
      flexWrap: 'wrap'
    }
  };

  return (
    <StandardQuickFilters
      filters={organizationFilters}
      title="Organization Quick Filters"
      activeFilters={activeFilters}
      onApplyFilter={onApplyFilter}
      customStyles={customStyles}
    />
  );
};

export default OrganizationQuickFilters; 